import React from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {Hotel} from '@findhotel/sapi'
import {useUrlUpdater} from 'components/data/WithUrlUpdater'
import {getCurrencyCode} from 'modules/meta/selectors'
import {droppedMetaLandingParams} from 'modules/search/constants'
import {getIsLandingItinerary} from 'modules/search/selectors'

import {Button} from '@vio/atlas/Button'
import {Box} from '@vio/atlas/helpers/Box'
import {cssTheme} from '@vio/atlas/src/themes'
import {Text} from '@vio/atlas/Text'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {PriceComposition} from '@vio/shared/src/search/PriceComposition'

const MapWrapper = styled.div`
  .--from-price {
    display: inline-block !important;
  }
`

const MobileWrapper = styled.div`
  padding: ${cssTheme.layout.spacing.s400};
  padding-top: ${cssTheme.layout.spacing.s300};
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  background-color: ${cssTheme.colors.background.brand.tertiary};
  border-color: ${cssTheme.colors.border.brand.tertiary};
  border-width: 1px;
  border-style: solid;
  border-radius: ${cssTheme.layout.radius.md};
  border-top: 0;
  border-top-right-radius: 0;
  border-top-left-radius: 0;

  .--from-price {
    display: inline-block !important;
  }
`

const DesktopWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  .--from-price {
    display: inline-block !important;
  }
`

interface Props {
  hotelId: Hotel['objectID'] | undefined
  price: number | undefined
  variant?: 'mobile' | 'desktop' | 'map'
}
export const HistoricalPriceOffer = ({
  hotelId,
  price,
  variant = 'desktop'
}: Props) => {
  const currencyCode = useSelector(getCurrencyCode)
  // The offer is dateless, so only a dateless landing can match
  const canCarryLandingToken = useSelector(state =>
    getIsLandingItinerary(state, hotelId)
  )
  const {updateLocation} = useUrlUpdater()

  const handleCheckAvailability = () => {
    updateLocation({
      hotelId,
      userSearch: '1',
      overlay: undefined,
      ...(!canCarryLandingToken && droppedMetaLandingParams)
    })
  }

  if (!hotelId || !price) return null

  if (variant === 'map') {
    return (
      <MapWrapper>
        <Text tag="div" variant="bodyXS" colorPath="content.neutral.secondary">
          <FormattedMessageWrapper
            id="datelessSRP.fromPriceNight"
            defaultMessage="from {price} / night"
            values={{
              price: (
                <PriceComposition
                  className="--from-price"
                  price={price}
                  anchorPrice={undefined}
                  currencyCode={currencyCode}
                />
              )
            }}
          />
        </Text>
      </MapWrapper>
    )
  }

  if (variant === 'mobile') {
    return (
      <MobileWrapper>
        <Box display="flex" alignItems="flex-end" gap="s200">
          <Text
            tag="div"
            variant="bodyXS"
            colorPath="content.neutral.secondary"
          >
            <FormattedMessageWrapper
              id="datelessSRP.fromPrice"
              defaultMessage="from {price}"
              values={{
                price: (
                  <PriceComposition
                    className="--from-price"
                    price={price}
                    anchorPrice={undefined}
                    currencyCode={currencyCode}
                  />
                )
              }}
            />
          </Text>
        </Box>
        <Box marginTop="s300">
          <Button
            onClick={handleCheckAvailability}
            dataId="CheckAvailability"
            size="lg"
          >
            <FormattedMessageWrapper
              id="checkAvailability"
              defaultMessage="Check availability"
            />
          </Button>
        </Box>
      </MobileWrapper>
    )
  }

  return (
    <DesktopWrapper>
      <Text tag="div" variant="bodyXS" colorPath="content.neutral.secondary">
        <FormattedMessageWrapper
          id="datelessSRP.fromPriceNight"
          defaultMessage="from {price} / night"
          values={{
            price: (
              <PriceComposition
                className="--from-price"
                price={price}
                anchorPrice={undefined}
                currencyCode={currencyCode}
              />
            )
          }}
        />
      </Text>
      <Box marginTop="s400">
        <Button
          onClick={handleCheckAvailability}
          fullWidth
          dataId="CheckAvailability"
          size="lg"
        >
          <FormattedMessageWrapper
            id="checkAvailability"
            defaultMessage="Check availability"
          />
        </Button>
      </Box>
    </DesktopWrapper>
  )
}
